import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveChannel } from '../../hooks/useLiveChannel';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Kunden — der gemeinsame Vertriebs-Trichter von Rafael und den Sales-Partnern.
 *
 * Vorher eine flache Liste mit fuenf Zustaenden ("kontaktiert", "Kunde", …).
 * Damit liess sich die eine Frage nicht beantworten, die im Vertrieb zaehlt:
 * was ist gerade offen und wer ist als naechstes dran? Deshalb jetzt die
 * Stufen des echten Ablaufs (Rafael, 02.09.2026) plus Produkt, Besitz und
 * Wiedervorlage — und die drei Ansichten, die sich daraus ergeben: alles,
 * nur meine, und was heute faellig ist.
 *
 * Sehen darf jeder alles (damit niemand dieselbe Firma zweimal anspricht),
 * aendern nur der Besitzer und Rafael — der Server erzwingt das, diese Ansicht
 * zeigt es nur an.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

type SalesStufe =
  | 'notiert' | 'angesprochen' | 'unterlagen' | 'erstbericht-offen'
  | 'geliefert' | 'erstgespraech' | 'auftrag' | 'kein-bedarf';

type SalesProdukt = 'offen' | 'report' | 'energy' | 'beides';

type SalesPrio = 'A' | 'B' | 'C' | 'D';

type VerlaufArt =
  | 'mail-raus' | 'mail-rein' | 'telefon' | 'treffen'
  | 'unterlagen' | 'bericht' | 'notiz';

interface VerlaufEintrag {
  id: string;
  datum: string;
  art: VerlaufArt;
  text: string;
  von: string;
  createdAt: string;
}

interface SalesDatei {
  id: string;
  name: string;
  groesse: number;
  von: string;
  hochgeladenAm: string;
}

interface SalesContact {
  id: string;
  firma: string;
  kontaktperson?: string;
  rolle?: string;
  email?: string;
  telefon?: string;
  netzwerk?: string;
  branche?: string;
  standort?: string;
  prio?: SalesPrio;
  folgegeschaeft?: string;
  produkt: SalesProdukt;
  stufe: SalesStufe;
  besitzer: string;
  angelegtVon: string;
  letzterKontakt?: string;
  wiedervorlage?: string;
  notiz?: string;
  verlauf: VerlaufEintrag[];
  dateien: SalesDatei[];
  createdAt: string;
  updatedAt: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STUFEN: { value: SalesStufe; label: string; kurz: string; color: string }[] = [
  { value: 'notiert',           label: 'Notiert (noch nicht angesprochen)', kurz: 'Notiert',      color: 'var(--tnf-565f89)' },
  { value: 'angesprochen',      label: 'Angesprochen',                      kurz: 'Angesprochen', color: 'var(--tnf-7aa2f7)' },
  { value: 'unterlagen',        label: 'Unterlagen erhalten',               kurz: 'Unterlagen',   color: 'var(--tnf-7dcfff)' },
  { value: 'erstbericht-offen', label: 'Erstbericht offen',                 kurz: 'Bericht offen', color: 'var(--tnf-e0af68)' },
  { value: 'geliefert',         label: 'Bericht geliefert',                 kurz: 'Geliefert',    color: 'var(--tnf-bb9af7)' },
  { value: 'erstgespraech',     label: 'Erstgespräch geführt',              kurz: 'Erstgespräch', color: 'var(--tnf-73daca)' },
  { value: 'auftrag',           label: 'Auftrag',                           kurz: 'Auftrag',      color: 'var(--tnf-9ece6a)' },
  { value: 'kein-bedarf',       label: 'Kein Bedarf (vorerst)',             kurz: 'Kein Bedarf',  color: 'var(--tnf-f7768e)' },
];

const PRODUKTE: { value: SalesProdukt; label: string }[] = [
  { value: 'offen',  label: 'offen' },
  { value: 'report', label: 'Report' },
  { value: 'energy', label: 'Energy' },
  { value: 'beides', label: 'beides' },
];

const VERLAUF_ARTEN: { value: VerlaufArt; label: string; icon: string }[] = [
  { value: 'mail-raus',  label: 'Mail geschickt',    icon: '↗' },
  { value: 'mail-rein',  label: 'Mail bekommen',     icon: '↙' },
  { value: 'telefon',    label: 'Telefonat',         icon: '☎' },
  { value: 'treffen',    label: 'Treffen',           icon: '◎' },
  { value: 'unterlagen', label: 'Unterlagen raus',   icon: '⎘' },
  { value: 'bericht',    label: 'Bericht geliefert', icon: '✓' },
  { value: 'notiz',      label: 'Notiz',             icon: '·' },
];

const PRIO_FARBEN: Record<SalesPrio, string> = {
  A: 'var(--tnf-9ece6a)',  // heiss
  B: 'var(--tnf-7aa2f7)',  // solid
  C: 'var(--tnf-565f89)',  // sekundaer
  D: 'var(--tnf-bb9af7)',  // Multiplikator
};

const API = '/api/partner';

/**
 * Kennungen, hinter denen ein Admin steckt. Ein Kontakt, der hier liegt, ist
 * noch nicht zugeteilt und kann uebernommen werden. Die Wahrheit steht im
 * Server (Rolle aus users.json); der Client braucht nur einen Anhaltspunkt,
 * um den Knopf anzubieten — abgelehnt wird notfalls serverseitig.
 */
const ADMIN_IDS = ['rafael', 'admin'];

type Ansicht = 'alle' | 'meine' | 'faellig';

function stufeMeta(stufe: SalesStufe) {
  const found = STUFEN.find(s => s.value === stufe);
  if (!found) {
    // Kann nur passieren, wenn der Server eine Stufe kennt, die diese Ansicht
    // nicht kennt — dann ist die Liste hier veraltet und das soll auffallen.
    throw new Error(`SalesPanel: unbekannte Stufe '${stufe}' — Stufenliste im Client veraltet?`);
  }
  return found;
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

function inTagen(tage: number): string {
  const d = new Date();
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
}

/**
 * Stufen, nach denen der Kunde am Zug ist — und damit die, bei denen eine
 * Wiedervorlage gehoert. Die Strategie nennt den Grund beim Namen: "Schick mir
 * Unterlagen" versandet, wenn niemand nachfasst; vorgesehen ist eine
 * freundliche Erinnerung nach einer Woche (Risiko 2).
 */
const STUFEN_MIT_NACHFASSEN: SalesStufe[] = ['angesprochen', 'unterlagen'];

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}


// ─── Verlauf ─────────────────────────────────────────────────────────────────

/**
 * Der Verlauf eines Kunden: was geschickt, telefoniert, geliefert wurde.
 * Genau das ersetzt die Telefonabsprache — was hier steht, koennen Rafael und
 * der Partner beide nachlesen, auch Monate spaeter. Deshalb nur anhaengen,
 * nie aendern.
 */
function VerlaufBlock({
  contact, editierbar, inputStyle, onAdd, onError,
}: {
  contact: SalesContact;
  editierbar: boolean;
  inputStyle: CSSProperties;
  onAdd: (id: string, art: VerlaufArt, text: string, datum: string) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [art, setArt] = useState<VerlaufArt>('mail-raus');
  const [text, setText] = useState('');
  const [datum, setDatum] = useState(heute());
  const [busy, setBusy] = useState(false);

  const eintraege = [...contact.verlauf].sort((a, b) =>
    b.datum.localeCompare(a.datum) || b.createdAt.localeCompare(a.createdAt));

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onAdd(contact.id, art, text, datum);
      setText('');
      setDatum(heute());
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        Verlauf
      </div>

      {eintraege.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 8 }}>
          Noch nichts festgehalten.
        </div>
      )}

      {eintraege.map(e => {
        const meta = VERLAUF_ARTEN.find(a => a.value === e.art);
        return (
          <div
            key={e.id}
            data-ai-id={`sales-verlauf-${e.id}`}
            style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 5, alignItems: 'baseline' }}
          >
            <span style={{ color: 'var(--tn-text-muted)', whiteSpace: 'nowrap', minWidth: 62 }}>
              {formatDate(e.datum)}
            </span>
            <span style={{ color: 'var(--tn-blue)', whiteSpace: 'nowrap', minWidth: 118 }}>
              {meta?.icon} {meta?.label ?? e.art}
            </span>
            <span style={{ color: 'var(--tn-text)', flex: 1 }}>{e.text}</span>
            <span style={{ color: 'var(--tn-text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{e.von}</span>
          </div>
        );
      })}

      {editierbar && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            data-ai-id={`sales-verlauf-datum-${contact.id}`}
            type="date"
            value={datum}
            onChange={e => setDatum(e.target.value)}
            style={{ ...inputStyle, padding: '4px 7px', fontSize: 12 }}
          />
          <select
            data-ai-id={`sales-verlauf-art-${contact.id}`}
            value={art}
            onChange={e => setArt(e.target.value as VerlaufArt)}
            style={{ ...inputStyle, padding: '4px 7px', fontSize: 12 }}
          >
            {VERLAUF_ARTEN.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <input
            data-ai-id={`sales-verlauf-text-${contact.id}`}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            placeholder="Was ist passiert?"
            style={{ ...inputStyle, padding: '4px 7px', fontSize: 12, flex: 1, minWidth: 200 }}
          />
          <button
            data-ai-id={`sales-verlauf-add-${contact.id}`}
            onClick={submit}
            disabled={busy || !text.trim()}
            style={{
              padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 700,
              cursor: busy || !text.trim() ? 'not-allowed' : 'pointer',
              background: busy || !text.trim() ? 'var(--tn-surface)' : 'var(--tn-blue)',
              color: busy || !text.trim() ? 'var(--tn-text-muted)' : '#fff',
              border: '1px solid var(--tn-border)',
            }}
          >
            {busy ? '…' : 'Eintragen'}
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * Die Ablage eines Kunden — was er geschickt hat und was zurueckgegangen ist.
 *
 * Der Verkaufsablauf haengt daran: "Kunde schickt Unterlagen" ist der Filter,
 * an dem sich entscheidet, ob wir Zeit investieren. Wenn die Unterlagen in
 * einem Mailpostfach liegen, weiss der andere nicht, dass sie da sind.
 */
function DateiBlock({
  contact, editierbar, onAendern, onError,
}: {
  contact: SalesContact;
  editierbar: boolean;
  onAendern: () => void;
  onError: (msg: string) => void;
}) {
  const [laedt, setLaedt] = useState(false);

  const hochladen = async (datei: File) => {
    setLaedt(true);
    try {
      const form = new FormData();
      form.append('datei', datei);
      const res = await fetch(`${API}/sales/${contact.id}/dateien`, { method: 'POST', body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      onAendern();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setLaedt(false);
    }
  };

  const entfernen = async (dateiId: string) => {
    try {
      const res = await fetch(`${API}/sales/${contact.id}/dateien/${dateiId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      onAendern();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        Ablage
      </div>

      {contact.dateien.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 8 }}>
          {editierbar
            ? 'Noch nichts abgelegt. Hier kommen die Unterlagen des Kunden hin — und der fertige Bericht zurück.'
            : 'Nichts abgelegt.'}
        </div>
      )}

      {contact.dateien.map(d => (
        <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12, marginBottom: 4 }}>
          {editierbar ? (
            <a
              data-ai-id={`sales-datei-${d.id}`}
              href={`${API}/sales/${contact.id}/dateien/${d.id}`}
              download
              style={{ color: 'var(--tn-blue)', textDecoration: 'none', flex: 1 }}
            >
              {d.name}
            </a>
          ) : (
            <span style={{ color: 'var(--tn-text-muted)', flex: 1 }}>{d.name}</span>
          )}
          <span style={{ color: 'var(--tn-text-muted)', whiteSpace: 'nowrap' }}>
            {Math.max(1, Math.round(d.groesse / 1024))} KB · {formatDate(d.hochgeladenAm.slice(0, 10))} · {d.von}
          </span>
          {editierbar && (
            <button
              data-ai-id={`sales-datei-weg-${d.id}`}
              onClick={() => entfernen(d.id)}
              title="Entfernen"
              style={{
                background: 'transparent', border: 'none', color: 'var(--tn-text-muted)',
                cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 4px',
              }}
            >
              ×
            </button>
          )}
        </div>
      ))}

      {editierbar && (
        <label
          data-ai-id={`sales-datei-upload-${contact.id}`}
          style={{
            display: 'inline-block', marginTop: 8, padding: '5px 14px', borderRadius: 5,
            fontSize: 12, fontWeight: 600, cursor: laedt ? 'progress' : 'pointer',
            background: 'var(--tn-surface)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
          }}
        >
          {laedt ? 'Lädt hoch…' : '+ Datei ablegen'}
          <input
            type="file"
            disabled={laedt}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) hochladen(f);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
        </label>
      )}

      {!editierbar && contact.dateien.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 4 }}>
          Herunterladen kann nur {contact.besitzer} — Kundenunterlagen bleiben bei dem, dem der Kunde gehört.
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

export default function SalesPanel() {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const isAdmin = user?.role === 'admin';

  const [contacts, setContacts] = useState<SalesContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ansicht, setAnsicht] = useState<Ansicht>('alle');
  const [suche, setSuche] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [rowError, setRowError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    firma: '', kontaktperson: '', rolle: '', email: '', telefon: '',
    netzwerk: '', branche: '', standort: '', prio: '' as SalesPrio | '',
    produkt: 'offen' as SalesProdukt, stufe: 'notiert' as SalesStufe,
    wiedervorlage: '', notiz: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = (raw as { contacts?: unknown[] }).contacts;
      if (!Array.isArray(list)) throw new Error('API /api/partner/sales: erwartet { contacts: [] }');
      const data = list.map((item: unknown) =>
        validateApiResponse<SalesContact>(item, '/api/partner/sales[item]', {
          id: 'string',
          firma: 'string',
          kontaktperson: { type: 'string', optional: true },
          rolle: { type: 'string', optional: true },
          email: { type: 'string', optional: true },
          telefon: { type: 'string', optional: true },
          netzwerk: { type: 'string', optional: true },
          branche: { type: 'string', optional: true },
          standort: { type: 'string', optional: true },
          prio: { type: 'string', optional: true },
          folgegeschaeft: { type: 'string', optional: true },
          produkt: 'string',
          stufe: 'string',
          besitzer: 'string',
          angelegtVon: 'string',
          letzterKontakt: { type: 'string', optional: true },
          wiedervorlage: { type: 'string', optional: true },
          notiz: { type: 'string', optional: true },
          verlauf: 'array',
          dateien: 'array',
          createdAt: 'string',
          updatedAt: 'string',
        }),
      );
      setContacts(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // Der Trichter ist zu zweit gefuehrt: traegt Rafael einen Kunden ein, muss er
  // bei Markus erscheinen, ohne dass jemand neu laedt. Der Server sendet die
  // Ereignisse seit jeher — es hat nur nie jemand zugehoert.
  useLiveChannel({
    label: 'SalesFunnel',
    onResync: () => { fetchContacts(); },
    onMessage: (msg: { type?: string }) => {
      if (msg.type === 'partner-sales-new' || msg.type === 'partner-sales-updated'
          || msg.type === 'partner-sales-deleted') {
        fetchContacts();
      }
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!form.firma.trim()) {
      setSubmitError('Firma ist ein Pflichtfeld');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API}/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firma: form.firma,
          kontaktperson: form.kontaktperson || undefined,
          rolle: form.rolle || undefined,
          email: form.email || undefined,
          telefon: form.telefon || undefined,
          netzwerk: form.netzwerk || undefined,
          branche: form.branche || undefined,
          standort: form.standort || undefined,
          prio: form.prio || undefined,
          produkt: form.produkt,
          stufe: form.stufe,
          wiedervorlage: form.wiedervorlage || undefined,
          notiz: form.notiz || undefined,
        }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const created = await res.json().catch(() => null) as { hinweis?: string } | null;
      if (created?.hinweis) setRowError(created.hinweis);
      await fetchContacts();
      setForm({
        firma: '', kontaktperson: '', rolle: '', email: '', telefon: '',
        netzwerk: '', branche: '', standort: '', prio: '',
        produkt: 'offen', stufe: 'notiert', wiedervorlage: '', notiz: '',
      });
      setShowForm(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [form, fetchContacts]);

  const patchContact = useCallback(async (id: string, patch: Partial<SalesContact>) => {
    setRowError('');
    // Optimistisch: der Trichter soll sich anfuehlen wie eine Liste, nicht wie
    // ein Formular. Schlaegt der Server fehl, holt fetchContacts den Stand zurueck.
    setContacts(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
    try {
      const res = await fetch(`${API}/sales/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
      fetchContacts();
    }
  }, [fetchContacts]);

  const addVerlauf = useCallback(async (id: string, art: VerlaufArt, text: string, datum: string) => {
    setRowError('');
    const res = await fetch(`${API}/sales/${id}/verlauf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ art, text, datum: datum || undefined }),
      signal: AbortSignal.timeout(15000),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    await fetchContacts();
  }, [fetchContacts]);

  const darfAendern = useCallback(
    (c: SalesContact) => isAdmin || c.besitzer === userId,
    [isAdmin, userId],
  );

  // Die uebernommene Master-Leadliste gehoert Rafael. Ohne einen Weg, einen
  // Kontakt an sich zu nehmen, waere der Trichter fuer einen Partner eine reine
  // Leseliste. Uebernehmen geht nur bei Kontakten, die noch beim Admin liegen —
  // fremde Kunden anderer Partner bleiben tabu (der Server erzwingt das).
  const uebernehmbar = useCallback(
    (c: SalesContact) => !isAdmin && c.besitzer !== userId && ADMIN_IDS.includes(c.besitzer),
    [isAdmin, userId],
  );

  const uebernehmen = useCallback(async (id: string) => {
    setRowError('');
    try {
      const res = await fetch(`${API}/sales/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ besitzer: userId }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      await fetchContacts();
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, fetchContacts]);

  const sichtbar = useMemo(() => {
    const today = heute();
    const q = suche.trim().toLowerCase();
    return contacts.filter(c => {
      if (ansicht === 'meine' && c.besitzer !== userId) return false;
      if (ansicht === 'faellig' && !(c.wiedervorlage && c.wiedervorlage <= today)) return false;
      if (!q) return true;
      // Bei 60+ Kontakten ist Scrollen keine Suche. Ueber alles, was man im
      // Kopf hat, wenn man jemanden sucht: Firma, Person, Ort, Branche, Netzwerk.
      return [c.firma, c.kontaktperson, c.rolle, c.branche, c.standort, c.netzwerk, c.notiz]
        .some(v => (v ?? '').toLowerCase().includes(q));
    });
  }, [contacts, ansicht, userId, suche]);

  const zaehler = useMemo(() => {
    const today = heute();
    const proStufe = new Map<SalesStufe, number>();
    for (const c of contacts) proStufe.set(c.stufe, (proStufe.get(c.stufe) ?? 0) + 1);
    return {
      proStufe,
      meine: contacts.filter(c => c.besitzer === userId).length,
      faellig: contacts.filter(c => !!c.wiedervorlage && c.wiedervorlage <= today).length,
      offeneBerichte: contacts.filter(c => c.stufe === 'erstbericht-offen').length,
      offeneBerichteMeine: contacts.filter(c => c.stufe === 'erstbericht-offen' && c.besitzer === userId).length,
    };
  }, [contacts, userId]);

  const inputStyle: CSSProperties = {
    padding: '7px 9px', borderRadius: 5, fontSize: 13, fontFamily: 'inherit',
    background: 'var(--tn-bg)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
  };
  const thStyle: CSSProperties = {
    textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 700,
    color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4,
    borderBottom: '1px solid var(--tn-border)', whiteSpace: 'nowrap',
  };
  const tdStyle: CSSProperties = {
    padding: '7px 10px', fontSize: 13, color: 'var(--tn-text)',
    borderBottom: '1px solid var(--tn-border)', verticalAlign: 'top',
  };

  return (
    <div className="cui-sales-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-bg)' }}>
      {/* Kopf */}
      <div style={{
        padding: '10px 16px', borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--tn-text)' }}>Kunden</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {([
            { value: 'alle' as Ansicht, label: `Alle (${contacts.length})` },
            { value: 'meine' as Ansicht, label: `Meine (${zaehler.meine})` },
            { value: 'faellig' as Ansicht, label: `Heute dran (${zaehler.faellig})` },
          ]).map(a => (
            <button
              key={a.value}
              data-ai-id={`sales-view-${a.value}`}
              onClick={() => setAnsicht(a.value)}
              style={{
                padding: '5px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                fontWeight: ansicht === a.value ? 700 : 500,
                background: ansicht === a.value ? 'var(--tn-blue)' : 'var(--tn-surface)',
                color: ansicht === a.value ? '#fff' : 'var(--tn-text)',
                border: '1px solid var(--tn-border)',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
        <input
          data-ai-id="sales-search"
          value={suche}
          onChange={e => setSuche(e.target.value)}
          placeholder="Suchen — Firma, Person, Ort, Branche…"
          style={{
            flex: 1, minWidth: 180, maxWidth: 340, padding: '6px 10px', borderRadius: 5, fontSize: 12,
            fontFamily: 'inherit', background: 'var(--tn-surface)', color: 'var(--tn-text)',
            border: '1px solid var(--tn-border)',
          }}
        />
        <span style={{ fontSize: 12, color: zaehler.offeneBerichte > 0 ? 'var(--tnf-e0af68)' : 'var(--tn-text-muted)' }}>
          {zaehler.offeneBerichte} Erstbericht{zaehler.offeneBerichte === 1 ? '' : 'e'} offen
        </span>
        <button
          data-ai-id="sales-new-contact"
          onClick={() => setShowForm(v => !v)}
          style={{
            padding: '6px 14px', borderRadius: 5, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: showForm ? 'var(--tn-surface)' : 'var(--tn-blue)',
            color: showForm ? 'var(--tn-text)' : '#fff', border: '1px solid var(--tn-border)',
          }}
        >
          {showForm ? 'Abbrechen' : '+ Kontakt'}
        </button>
      </div>

      {/* Trichter-Zeile: wie viele stehen auf welcher Stufe */}
      <div style={{
        display: 'flex', gap: 6, padding: '8px 16px', flexWrap: 'wrap',
        borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
      }}>
        {STUFEN.map(s => (
          <div
            key={s.value}
            data-ai-id={`sales-funnel-count-${s.value}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              borderRadius: 12, background: 'var(--tn-surface)', border: '1px solid var(--tn-border)',
              fontSize: 12, color: 'var(--tn-text-muted)',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.kurz}
            <strong style={{ color: 'var(--tn-text)' }}>{zaehler.proStufe.get(s.value) ?? 0}</strong>
          </div>
        ))}
      </div>

      {/* Neuer Kontakt */}
      {showForm && (
        <div style={{
          padding: 16, borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
          background: 'var(--tn-bg-dark)', display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        }}>
          <input data-ai-id="sales-form-firma" style={inputStyle} placeholder="Firma *"
            value={form.firma} onChange={e => setForm(f => ({ ...f, firma: e.target.value }))} />
          <input data-ai-id="sales-form-kontaktperson" style={inputStyle} placeholder="Kontaktperson"
            value={form.kontaktperson} onChange={e => setForm(f => ({ ...f, kontaktperson: e.target.value }))} />
          <input data-ai-id="sales-form-rolle" style={inputStyle} placeholder="Funktion (z. B. Geschäftsführer)"
            value={form.rolle} onChange={e => setForm(f => ({ ...f, rolle: e.target.value }))} />
          <input data-ai-id="sales-form-email" style={inputStyle} placeholder="E-Mail"
            value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <input data-ai-id="sales-form-telefon" style={inputStyle} placeholder="Telefon"
            value={form.telefon} onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))} />
          <input data-ai-id="sales-form-netzwerk" style={inputStyle} placeholder="Netzwerk (woher kennen wir ihn?)"
            value={form.netzwerk} onChange={e => setForm(f => ({ ...f, netzwerk: e.target.value }))} />
          <input data-ai-id="sales-form-branche" style={inputStyle} placeholder="Branche / Kerngeschäft"
            value={form.branche} onChange={e => setForm(f => ({ ...f, branche: e.target.value }))} />
          <input data-ai-id="sales-form-standort" style={inputStyle} placeholder="Standort"
            value={form.standort} onChange={e => setForm(f => ({ ...f, standort: e.target.value }))} />
          <select data-ai-id="sales-form-prio" style={inputStyle}
            value={form.prio} onChange={e => setForm(f => ({ ...f, prio: e.target.value as SalesPrio | '' }))}>
            <option value="">Priorität: offen</option>
            <option value="A">A — heiß</option>
            <option value="B">B — solid</option>
            <option value="C">C — sekundär</option>
            <option value="D">D — Multiplikator</option>
          </select>
          <select data-ai-id="sales-form-produkt" style={inputStyle}
            value={form.produkt} onChange={e => setForm(f => ({ ...f, produkt: e.target.value as SalesProdukt }))}>
            {PRODUKTE.map(p => <option key={p.value} value={p.value}>Produkt: {p.label}</option>)}
          </select>
          <select data-ai-id="sales-form-stufe" style={inputStyle}
            value={form.stufe} onChange={e => setForm(f => ({ ...f, stufe: e.target.value as SalesStufe }))}>
            {STUFEN.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--tn-text-muted)' }}>
            Wiedervorlage
            <input data-ai-id="sales-form-wiedervorlage" type="date" style={inputStyle}
              value={form.wiedervorlage} onChange={e => setForm(f => ({ ...f, wiedervorlage: e.target.value }))} />
          </label>
          <input data-ai-id="sales-form-notiz" style={{ ...inputStyle, gridColumn: '1 / -1' }} placeholder="Notiz"
            value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))} />
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              data-ai-id="sales-form-submit"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '8px 20px', borderRadius: 5, fontSize: 13, fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                background: 'var(--tn-blue)', color: '#fff', border: 'none',
              }}
            >
              {submitting ? 'Speichert…' : 'Anlegen'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
              Wer einträgt, dem gehört der Kunde.
            </span>
            {submitError && <span style={{ fontSize: 12, color: 'var(--tnf-f7768e)' }}>{submitError}</span>}
          </div>
        </div>
      )}

      {rowError && (
        <div style={{
          padding: '8px 16px', fontSize: 12, color: 'var(--tnf-f7768e)', flexShrink: 0,
          background: 'rgba(247,118,142,0.1)', borderBottom: '1px solid var(--tnf-f7768e)',
        }}>
          {rowError}
        </div>
      )}

      {/* Liste */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <div style={{ padding: 20, color: 'var(--tn-text-muted)', fontSize: 13 }}>Lädt…</div>}

        {error && (
          <div style={{ margin: 16, padding: 12, borderRadius: 6, background: 'rgba(247,118,142,0.12)', border: '1px solid var(--tnf-f7768e)', color: 'var(--tnf-f7768e)', fontSize: 13 }}>
            Kontakte konnten nicht geladen werden: {error}
          </div>
        )}

        {!loading && !error && sichtbar.length === 0 && (
          <div style={{ padding: 20, color: 'var(--tn-text-muted)', fontSize: 13 }}>
            {contacts.length === 0
              ? 'Noch kein Kontakt eingetragen. Mit „+ Kontakt" fängt der Trichter an.'
              : 'In dieser Ansicht steht gerade nichts.'}
          </div>
        )}

        {!loading && !error && sichtbar.length > 0 && (
          <table className="cui-sales-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Firma</th>
                <th style={thStyle}>Kontakt</th>
                <th style={thStyle}>Produkt</th>
                <th style={thStyle}>Stufe</th>
                <th style={thStyle}>Wiedervorlage</th>
                <th style={thStyle}>Letzter Kontakt</th>
                <th style={thStyle}>Gehört</th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.map(c => {
                const meta = stufeMeta(c.stufe);
                const editierbar = darfAendern(c);
                const faellig = !!c.wiedervorlage && c.wiedervorlage <= heute();
                const offen = openId === c.id;
                return [
                  <tr
                    key={c.id}
                    data-ai-id={`sales-row-${c.id}`} data-expanded={offen}
                    onClick={() => setOpenId(offen ? null : c.id)}
                    style={{ cursor: 'pointer', background: offen ? 'var(--tn-bg-dark)' : 'transparent' }}
                  >
                    <td data-label="Firma" style={{ ...tdStyle, fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {c.prio && (
                          <span
                            title={`Priorität ${c.prio}`}
                            style={{
                              fontSize: 10, fontWeight: 800, color: PRIO_FARBEN[c.prio],
                              border: `1px solid ${PRIO_FARBEN[c.prio]}`, borderRadius: 3,
                              padding: '0 4px', lineHeight: '15px',
                            }}
                          >
                            {c.prio}
                          </span>
                        )}
                        <button className="cui-sales-expand" aria-expanded={offen} onClick={e => { e.stopPropagation(); setOpenId(offen ? null : c.id); }}>{offen ? '▾' : '▸'} {c.firma}</button>
                      </span>
                      {(c.branche || c.standort || c.netzwerk) && (
                        <div className="cui-sales-secondary" style={{ fontSize: 11, color: 'var(--tn-text-muted)', fontWeight: 400 }}>
                          {[c.branche, c.standort, c.netzwerk].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td data-label="Kontakt" style={tdStyle}>
                      {c.kontaktperson || '—'}
                      {c.rolle && <div style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>{c.rolle}</div>}
                    </td>
                    <td data-label="Produkt" style={tdStyle}>
                      <select
                        data-ai-id={`sales-produkt-${c.id}`}
                        value={c.produkt}
                        disabled={!editierbar}
                        onClick={e => e.stopPropagation()}
                        onChange={e => patchContact(c.id, { produkt: e.target.value as SalesProdukt })}
                        style={{
                          ...inputStyle, padding: '3px 6px', fontSize: 12,
                          opacity: editierbar ? 1 : 0.6, cursor: editierbar ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {PRODUKTE.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Stufe" style={tdStyle}>
                      <select
                        data-ai-id={`sales-stufe-${c.id}`}
                        value={c.stufe}
                        disabled={!editierbar}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          const neu = e.target.value as SalesStufe;
                          const patch: Partial<SalesContact> = {
                            stufe: neu,
                            // Eine Stufenaenderung IST ein Kontakt — das Datum von
                            // Hand nachzupflegen vergisst jeder.
                            letzterKontakt: heute(),
                          };
                          // Wiedervorlage nur vorschlagen, wo noch keine steht —
                          // eine gesetzte Erinnerung zu ueberschreiben waere
                          // schlimmer als keine.
                          if (STUFEN_MIT_NACHFASSEN.includes(neu) && !c.wiedervorlage) {
                            patch.wiedervorlage = inTagen(7);
                          }
                          if (neu === 'angesprochen' && zaehler.offeneBerichteMeine > 0) {
                            setRowError(
                              `Hinweis: ${zaehler.offeneBerichteMeine} Erstbericht${zaehler.offeneBerichteMeine === 1 ? '' : 'e'} `
                              + 'von dir ist noch offen. Die Strategie sagt: erst liefern, dann den Nächsten ansprechen — '
                              + 'sonst frisst die Wartezeit den Eindruck.',
                            );
                          }
                          patchContact(c.id, patch);
                        }}
                        style={{
                          ...inputStyle, padding: '3px 6px', fontSize: 12, color: meta.color, fontWeight: 700,
                          opacity: editierbar ? 1 : 0.6, cursor: editierbar ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {STUFEN.map(s => <option key={s.value} value={s.value}>{s.kurz}</option>)}
                      </select>
                    </td>
                    <td data-label="Wiedervorlage" style={{ ...tdStyle, color: faellig ? 'var(--tnf-e0af68)' : 'var(--tn-text)', whiteSpace: 'nowrap' }}>
                      <input
                        data-ai-id={`sales-wiedervorlage-${c.id}`}
                        type="date"
                        value={c.wiedervorlage ?? ''}
                        disabled={!editierbar}
                        onClick={e => e.stopPropagation()}
                        onChange={e => patchContact(c.id, { wiedervorlage: e.target.value })}
                        style={{
                          ...inputStyle, padding: '3px 6px', fontSize: 12,
                          color: faellig ? 'var(--tnf-e0af68)' : 'var(--tn-text)',
                          opacity: editierbar ? 1 : 0.6,
                        }}
                      />
                    </td>
                    <td data-label="Letzter Kontakt" style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--tn-text-muted)' }}>
                      {formatDate(c.letzterKontakt)}
                    </td>
                    <td data-label="Gehört" style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--tn-text-muted)' }}>
                      {c.besitzer === userId ? 'mir' : c.besitzer}
                      {uebernehmbar(c) && (
                        <button
                          data-ai-id={`sales-uebernehmen-${c.id}`}
                          onClick={e => { e.stopPropagation(); uebernehmen(c.id); }}
                          title="Diesen Kontakt übernehmen — danach kannst du ihn fortschreiben"
                          style={{
                            marginLeft: 8, padding: '2px 8px', borderRadius: 4, fontSize: 11,
                            cursor: 'pointer', background: 'var(--tn-surface)', color: 'var(--tn-blue)',
                            border: '1px solid var(--tn-blue)',
                          }}
                        >
                          übernehmen
                        </button>
                      )}
                    </td>
                  </tr>,
                  offen ? (
                    <tr key={`${c.id}-detail`} data-ai-id={`sales-detail-${c.id}`}>
                      <td colSpan={7} style={{ ...tdStyle, background: 'var(--tn-bg-dark)' }}>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, marginBottom: 8, color: 'var(--tn-text-muted)' }}>
                          {c.email && <span>E-Mail: <span style={{ color: 'var(--tn-text)' }}>{c.email}</span></span>}
                          {c.telefon && <span>Telefon: <span style={{ color: 'var(--tn-text)' }}>{c.telefon}</span></span>}
                          <span>angelegt von {c.angelegtVon}</span>
                        </div>
                        {/* Vor dem Erstbericht muss stehen, was der Kunde danach
                            will — der Server laesst die Stufe sonst nicht zu.
                            Deshalb steht das Feld ueber der Notiz, nicht darunter. */}
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, color: c.folgegeschaeft ? 'var(--tn-text-muted)' : 'var(--tnf-e0af68)', marginBottom: 3 }}>
                            Folgegeschäft — was will der Kunde danach? {c.folgegeschaeft ? '' : '(nötig vor einem Erstbericht)'}
                          </div>
                          <input
                            data-ai-id={`sales-folgegeschaeft-${c.id}`}
                            defaultValue={c.folgegeschaeft ?? ''}
                            disabled={!editierbar}
                            placeholder="z. B. Messkampagne, Report-Abo, Entwicklungspartnerschaft"
                            onBlur={e => {
                              if (!editierbar) return;
                              if (e.target.value === (c.folgegeschaeft ?? '')) return;
                              patchContact(c.id, { folgegeschaeft: e.target.value });
                            }}
                            style={{
                              ...inputStyle, width: '100%',
                              border: `1px solid ${c.folgegeschaeft ? 'var(--tn-border)' : 'var(--tnf-e0af68)'}`,
                            }}
                          />
                        </div>
                        <textarea
                          data-ai-id={`sales-notiz-${c.id}`}
                          defaultValue={c.notiz ?? ''}
                          disabled={!editierbar}
                          placeholder={editierbar ? 'Kurznotiz zum Kunden — was man über ihn wissen muss…' : 'Nur der Besitzer kann hier schreiben.'}
                          onBlur={e => {
                            if (!editierbar) return;
                            if (e.target.value === (c.notiz ?? '')) return;
                            patchContact(c.id, { notiz: e.target.value });
                          }}
                          rows={2}
                          style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                        />
                        {!editierbar && (
                          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 5 }}>
                            {uebernehmbar(c)
                              ? `Dieser Kunde liegt noch bei ${c.besitzer}. Mit „übernehmen" in der Zeile nimmst du ihn — danach kannst du Stufe, Verlauf und Ablage pflegen.`
                              : `Dieser Kunde gehört ${c.besitzer} — du siehst ihn, damit ihn niemand doppelt anspricht.`}
                          </div>
                        )}
                        <VerlaufBlock
                          contact={c}
                          editierbar={editierbar}
                          inputStyle={inputStyle}
                          onAdd={addVerlauf}
                          onError={setRowError}
                        />
                        <DateiBlock
                          contact={c}
                          editierbar={editierbar}
                          onAendern={fetchContacts}
                          onError={setRowError}
                        />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
